/**
 * Raport zarządu — teksty nowego wyglądu zakładek „Zamknięcie miesiąca” (`mbr.close.ui.*`) i „Ustawienia raportu”
 * (`mbr.close.settingsUi.*`). WŁAŚCICIEL: agent sekcji close. Wpięte przez mbr-close.ts (bez zmian w plikach fundamentu).
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const mbrCloseUiPl = {
  kpi: {
    formsApproved: 'Formularze zatwierdzone',
    formsApprovedNote: 'z {total}',
    formsOverdue: 'Formularze po terminie',
    checksFail: 'Uzgodnienia czerwone',
    failNote: 'blokują',
    failNoneNote: 'brak blokad',
    checksWarn: 'Uzgodnienia żółte',
  },
  process: {
    title: 'Przebieg zamknięcia',
    stepOf: 'Krok {n} z {total}',
    done: 'Zamknięcie zakończone',
  },
  steps: {
    open: {
      title: 'Otwarcie',
      done: 'Otwarte — formularze rozesłane',
      todo: 'Otwórz zamknięcie — odpowiedzialni dostaną powiadomienia',
    },
    forms: {
      title: 'Formularze działów',
      progress: 'Zatwierdzone {approved} z {total}',
    },
    checks: {
      title: 'Uzgodnienia',
      progress: 'Czerwone: {fail} · żółte: {warn}',
      lastRun: 'Przebieg zapisany {date}',
      none: 'Uruchom uzgodnienia, żeby zapisać przebieg',
    },
    freeze: {
      title: 'Zamrożenie',
      done: 'Zamrożony {date}',
      ready: 'Gotowe do zamrożenia',
      blocked: 'Czerwone uzgodnienia: {count} — świadome zatwierdzenie',
      todo: 'Po formularzach i uzgodnieniach',
    },
    send: {
      title: 'Wysyłka do zarządu',
      done: 'Wysłano {date}',
      planned: 'Plan: {date}',
      late: 'Termin minął {date}',
      afterFreeze: 'Po zamrożeniu miesiąca',
    },
  },
  stepState: { done: 'zrobione', current: 'teraz', todo: 'później', blocked: 'zablokowane' },
  gate: {
    overrideTitle: 'Zamrożenie wymaga świadomego zatwierdzenia',
    overrideBody:
      'Czerwone uzgodnienia: {count}. Wyjaśnij je albo zatwierdź z uzasadnieniem — trafi do komentarzy i audytu.',
    notApproved: 'Niezatwierdzone formularze: {count}. Zamrożenie zapisze dane w obecnej postaci.',
    clear: 'Nic nie blokuje — formularze zatwierdzone, bez czerwonych uzgodnień.',
    frozen: 'Miesiąc zamrożony — raport pokazuje migawkę. Zmiany wymagają ponownego otwarcia.',
    readOnly: 'Zamrożenie i ponowne otwarcie wykonuje osoba z uprawnieniem zamykania miesiąca.',
    freezeOverride: 'Zamroź z uzasadnieniem',
    notOpened: 'Zamknięcie nie jest jeszcze otwarte.',
  },
  forms: {
    title: 'Formularze działów',
    subtitle: 'Najpierw po terminie i zwrócone do poprawy, zatwierdzone na końcu.',
    due: 'termin {date}',
    dueToday: 'termin dziś',
    dueIn: 'zostało dni: {count}',
    late: 'po terminie, dni: {count}',
    progress: 'Postęp formularza',
    aiFirst: 'AI: zacznij tutaj',
    aiFirstHint: 'Wniosek AI: od tego formularza zacznij domykanie miesiąca.',
    checks: 'Uzgodnienia: {count}',
    all: 'Wszystkie',
    open: 'Do domknięcia',
  },
  ai: {
    title: 'Co blokuje zamknięcie',
    subtitle: 'Wnioski AI z formularzy i uzgodnień — każdy prowadzi do miejsca, w którym to poprawisz.',
    next: 'Następne kroki',
    draft: 'wersja robocza',
  },
  checksTitle: 'Uzgodnienia miesiąca (wszystkie sekcje)',
  wizard: {
    steps: { file: 'Plik', mapping: 'Podgląd mapowania', result: 'Wynik' },
    dropHint:
      'Plik .xlsx „raport miesiąc vs miesiąc”. Najpierw zobaczysz podgląd — nic się nie zapisze bez potwierdzenia.',
    change: 'Zmień plik',
    recognized: 'Rozpoznane arkusze: {count} z {total}',
    next: 'Dalej: podgląd',
    back: 'Wstecz',
    onlyErrors: 'Tylko nieczytelne',
    source: 'Skąd (arkusz, komórka)',
    target: 'Dokąd',
    value: 'Wartość',
    shown: 'Pokazano {shown} z {total} pól',
    resultTitle: 'Dane zapisane do zamknięcia',
    written: '{forms} formularzy · {metrics} wskaźników · {employees} pracowników',
    skipped: 'pominięty: {reason}',
    skipReason: { CLOSED: 'miesiąc zamrożony', NOT_IN_FILE: 'brak w pliku' },
    warningsAfter: 'Rozbieżności po zapisie',
    finish: 'Gotowe',
  },
};

export const mbrCloseUiEn: Shape<typeof mbrCloseUiPl> = {
  kpi: {
    formsApproved: 'Forms approved',
    formsApprovedNote: 'of {total}',
    formsOverdue: 'Forms overdue',
    checksFail: 'Red checks',
    failNote: 'blocking',
    failNoneNote: 'no blockers',
    checksWarn: 'Yellow checks',
  },
  process: { title: 'Close progress', stepOf: 'Step {n} of {total}', done: 'Close completed' },
  steps: {
    open: {
      title: 'Opening',
      done: 'Opened — forms sent out',
      todo: 'Open the close — owners get notified',
    },
    forms: { title: 'Department forms', progress: 'Approved {approved} of {total}' },
    checks: {
      title: 'Reconciliation',
      progress: 'Red: {fail} · yellow: {warn}',
      lastRun: 'Run saved {date}',
      none: 'Run the reconciliation to save a run',
    },
    freeze: {
      title: 'Freeze',
      done: 'Frozen {date}',
      ready: 'Ready to freeze',
      blocked: 'Red checks: {count} — conscious approval',
      todo: 'After forms and reconciliation',
    },
    send: {
      title: 'Board e-mail',
      done: 'Sent {date}',
      planned: 'Planned: {date}',
      late: 'Deadline passed {date}',
      afterFreeze: 'After the month is frozen',
    },
  },
  stepState: { done: 'done', current: 'now', todo: 'later', blocked: 'blocked' },
  gate: {
    overrideTitle: 'Freezing needs a conscious approval',
    overrideBody:
      'Red checks: {count}. Explain them or approve with a justification — it goes to the comments and the audit.',
    notApproved: 'Forms not approved: {count}. The freeze stores the data as it is.',
    clear: 'Nothing blocks — forms approved, no red checks.',
    frozen: 'Month frozen — the report shows the snapshot. Changes need a reopen.',
    readOnly: 'Freezing and reopening is done by a person allowed to close the month.',
    freezeOverride: 'Freeze with justification',
    notOpened: 'The close is not open yet.',
  },
  forms: {
    title: 'Department forms',
    subtitle: 'Overdue and returned first, approved last.',
    due: 'due {date}',
    dueToday: 'due today',
    dueIn: 'days left: {count}',
    late: 'overdue, days: {count}',
    progress: 'Form progress',
    aiFirst: 'AI: start here',
    aiFirstHint: 'AI insight: start closing the month with this form.',
    checks: 'Checks: {count}',
    all: 'All',
    open: 'To finish',
  },
  ai: {
    title: 'What blocks the close',
    subtitle: 'AI insights from forms and checks — each leads to the place where you fix it.',
    next: 'Next steps',
    draft: 'draft',
  },
  checksTitle: 'Month reconciliation (all sections)',
  wizard: {
    steps: { file: 'File', mapping: 'Mapping preview', result: 'Result' },
    dropHint:
      'The .xlsx "month vs month" report. You see a preview first — nothing is saved without confirmation.',
    change: 'Change file',
    recognized: 'Recognised sheets: {count} of {total}',
    next: 'Next: preview',
    back: 'Back',
    onlyErrors: 'Unreadable only',
    source: 'From (sheet, cell)',
    target: 'To',
    value: 'Value',
    shown: 'Showing {shown} of {total} fields',
    resultTitle: 'Data saved to the close',
    written: '{forms} forms · {metrics} indicators · {employees} employees',
    skipped: 'skipped: {reason}',
    skipReason: { CLOSED: 'month frozen', NOT_IN_FILE: 'not in the file' },
    warningsAfter: 'Discrepancies after saving',
    finish: 'Done',
  },
};

export const mbrSettingsUiPl = {
  rulesHint: 'Kiedy klient jest utracony lub „wahadłowy”, co jest „bez zmian” i od ilu godzin alarmujemy.',
  allocationHint: 'Jak koszty centrali rozkładają się na oddziały w wyniku oddziałów.',
  fleet: 'Progi aut w najmie',
  fleetUnderused: 'Auto „do przesunięcia” poniżej (% limitu km)',
  fleetOverLimit: 'Auto „ponad limit” od (% limitu km)',
  seasons: 'Sezony segmentów klientów',
  seasonsHint:
    'Miesiące bez zamówień, które dla segmentu są normalne (np. szkoły w wakacje) — taki klient nie trafi do utraconych.',
  segment: 'Segment',
  segmentPlaceholder: 'np. INSTITUTION',
  addSeason: 'Dodaj segment',
  removeSeason: 'Usuń segment',
  noSeasons: 'Brak sezonów — każdy miesiąc bez zamówienia liczy się normalnie.',
  ai: 'Progi AI insight',
  aiHint: 'Kiedy odchylenie jest wnioskiem AI. Wyższe progi = mniej, ale ważniejszych wniosków na zarządzie.',
  anomalyZ: 'Odchylenie od historii 12 mies. od (odporny z-score)',
  anomalyZHint: 'Np. 3 = wartość wyraźnie poza zwykłym zakresem miesięcy.',
  minAmount: 'Minimalna kwota wniosku (zł)',
  minAmountHint: 'Zmiana mniejsza niż ta kwota nie jest wnioskiem.',
  minCount: 'Minimalna liczność bazy (szt.)',
  minCountHint: 'Przy mniejszej bazie zmiana w sztukach, nie w procentach („+300%” z 1 na 4).',
  unsaved: 'Niezapisane zmiany',
  unsavedHint: 'Po zapisaniu zadziałają na wszystkie niezamknięte miesiące.',
  invalid: 'Pola poza zakresem: {list} — popraw zaznaczone na czerwono.',
  range: 'zakres {min}–{max}',
  saved: 'Zapisano {date}',
};

export const mbrSettingsUiEn: Shape<typeof mbrSettingsUiPl> = {
  rulesHint:
    'When a customer is lost or alternating, what counts as "no change" and from how many hours we alert.',
  allocationHint: 'How head-office costs are split across branches in the branch result.',
  fleet: 'Rented vehicle thresholds',
  fleetUnderused: 'Vehicle "to reassign" below (% of the km limit)',
  fleetOverLimit: 'Vehicle "over the limit" from (% of the km limit)',
  seasons: 'Customer segment seasons',
  seasonsHint:
    'Months without orders that are normal for a segment (e.g. schools in the holidays) — such a customer is not lost.',
  segment: 'Segment',
  segmentPlaceholder: 'e.g. INSTITUTION',
  addSeason: 'Add segment',
  removeSeason: 'Remove segment',
  noSeasons: 'No seasons — every month without an order counts as usual.',
  ai: 'AI insight thresholds',
  aiHint:
    'When a deviation becomes an AI insight. Higher thresholds = fewer, more important insights for the board.',
  anomalyZ: 'Deviation from the 12-month history from (robust z-score)',
  anomalyZHint: 'E.g. 3 = clearly outside the usual range of months.',
  minAmount: 'Minimum insight amount (PLN)',
  minAmountHint: 'A change below this amount is not an insight.',
  minCount: 'Minimum base count (pcs)',
  minCountHint: 'Below this base a change is shown in units, not percent ("+300%" from 1 to 4).',
  unsaved: 'Unsaved changes',
  unsavedHint: 'After saving they apply to every month that is not closed.',
  invalid: 'Fields out of range: {list} — fix the ones marked red.',
  range: 'range {min}–{max}',
  saved: 'Saved {date}',
};
